/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { Button as ButtonCore, type ButtonProps as ButtonCoreProps, type ButtonRenderProps, type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconContext } from '../icon/icon.component';
import { Text, TextContext, type TextProps } from '../text/text.component';

interface TextButtonProps extends Omit<ButtonCoreProps, 'style' | 'className' | 'isPending'> {
  /** Wether the button has safe area. (Default: none) */
  safeArea?: 'rounded' | 'square' | 'none';
  /** The variant of the button. (Default: primary) */
  variant?: keyof typeof safeAreaVariants;
  /** The size of the button. (Default: 32) */
  size?: keyof typeof sizeVariants;
  style?: stylex.StyleXStyles;
  UNSAFE_TextContext?: Parameters<typeof TextContext.Provider>[0]['value'] | ((renderProps: ButtonRenderProps) => Parameters<typeof TextContext.Provider>[0]['value']);
}

const TextButtonContext = createContext<ContextValue<TextButtonProps, HTMLButtonElement>>({});

const TextButton = forwardRef((props: TextButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, TextButtonContext);

  const { style, children, variant = 'primary', size = 32, safeArea = 'none', UNSAFE_TextContext, ...coreProps } = props;

  return (
    <ButtonCore
      ref={ref}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        safeArea !== 'none' && safeAreaSizeVariants[size],
        safeArea !== 'none' && safeAreaVariants[variant],
        safeArea === 'rounded' && safeAreaStyles.rounded,
        style,
      )}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(evaluatedChildren).every(c => !isValidElement(c));

        const contentColor = (
          renderProps.isDisabled && 'disabled' ||
          variant === 'primary' && 'interactive' ||
          variant === 'secondary' && 'primary' ||
          variant === 'tertiary' && renderProps.isHovered && 'primary' ||
          variant === 'tertiary' && 'tertiary' ||
          'secondary'
        );

        return (
          <Provider
            values={[
              [TextContext, {
                size: TEXT_SIZES[size],
                color: contentColor,
                ...(typeof UNSAFE_TextContext === 'function' ? UNSAFE_TextContext(renderProps) : UNSAFE_TextContext),
              }],
              [IconContext, {
                slots: {
                  [TEXT_BUTTON_SLOTS.LEFT_ICON]: {
                    style: iconStyles.base,
                    size: ICON_LEFT_SIZES[size],
                    color: contentColor,
                  },
                  [TEXT_BUTTON_SLOTS.RIGHT_ICON]: {
                    style: iconStyles.base,
                    size: ICON_RIGHT_SIZES[size],
                    color: contentColor,
                  },
                }
              }],
            ]}>
            {typeof evaluatedChildren === 'string' || isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
          </Provider>
        );
      }}
    </ButtonCore>
  );
});

const TEXT_SIZES: Record<keyof typeof safeAreaSizeVariants, TextProps['size']> = {
  28: 12,
  32: 13,
  40: 14,
  48: 16,
  52: 16,
  64: 20,
};

const ICON_LEFT_SIZES: Record<keyof typeof safeAreaSizeVariants, number> = {
  28: 12,
  32: 16,
  40: 16,
  48: 20,
  52: 20,
  64: 24,
};

const ICON_RIGHT_SIZES: Record<keyof typeof safeAreaSizeVariants, number> = {
  28: 12,
  32: 12,
  40: 12,
  48: 16,
  52: 16,
  64: 16,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderStyle: 'none',
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
      ':is([data-pending])': 'auto',
    },
    display: 'inline-flex',
    isolation: 'isolate',
    justifyContent: 'center',
    paddingInline: 0,
    position: 'relative',
    transitionDuration: '0.2s',
    transitionProperty: 'color, background-color',
    transitionTimingFunction: 'easy-in-out',
    userSelect: 'none',
  },
});

const safeAreaStyles = stylex.create({ rounded: { borderRadius: 999 } });

const safeAreaVariants = stylex.create({
  primary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceTertiary,
    },
  },
  secondary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceAlternativeHover,
    },
  },
  tertiary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceAlternativeHover,
    },
  },
});

const sizeVariants = stylex.create({
  28: { gap: 6 },
  32: { gap: 8 },
  40: { gap: 6 },
  48: { gap: 8 },
  52: { gap: 10 },
  64: { gap: 12 },
});

const safeAreaSizeVariants = stylex.create({
  28: {
    borderRadius: 6,
    height: 28,
    paddingInline: 12,
  },
  32: {
    borderRadius: 8,
    height: 32,
    paddingInline: 12,
  },
  40: {
    borderRadius: 8,
    height: 40,
    paddingInline: 16,
  },
  48: {
    borderRadius: 10,
    height: 48,
    paddingInline: 20,
  },
  52: {
    borderRadius: 10,
    height: 52,
    paddingInline: 28,
  },
  64: {
    borderRadius: 14,
    height: 64,
    paddingInline: 32,
  },
});

const iconStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'stroke, fill',
    transitionTimingFunction: 'easy-in-out',
  },
});

const TEXT_BUTTON_SLOTS = {
  LEFT_ICON: 'left-icon',
  RIGHT_ICON: 'right-icon',
};

export { TEXT_BUTTON_SLOTS, TextButton, TextButtonContext, type TextButtonProps };
